import React, { useState } from 'react';
import { Divider } from '../../components/sidebar/components/Divider';
import ScrollArea from '../../components/common/scrollArea';
import { useOrganization } from '../../hooks/useOrganization';
import { useUsers } from '../../hooks/useUsers';
import { toast } from 'react-toastify';
import read from '../../assets/read.svg';

interface Notification {
  type: string;
  title: string;
  time: string;
  isRead: boolean;
}

const NotificationsPage = () => {
  const { organization, updateOrganization, isLoading } = useOrganization();
  const { user } = useUsers();
  const [hasChanges, setHasChanges] = useState(false);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    slackNotifications: true,
    leadAlerts: true,
    weeklyDigest: true,
    accountUpdates: true
  });

  const [filter, setFilter] = useState('all');
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Get filtered notifications
  const getFilteredNotifications = () => {
    switch (filter) {
      case 'unread':
        return notifications.filter(n => !n.isRead);
      case 'recent':
        return [...notifications].sort((a, b) => 
          new Date(b.time).getTime() - new Date(a.time).getTime()
        );
      default:
        return notifications;
    }
  };

  const handleToggle = (setting: keyof typeof settings) => {
    const newSettings = {
      ...settings,
      [setting]: !settings[setting]
    };
    setSettings(newSettings);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updateOrganization({
        current_plan: organization?.current_plan
      });
      setHasChanges(false);
      toast.success('Notification settings updated successfully');
    } catch (error) {
      console.error('Error updating notification settings:', error);
      toast.error('Failed to update notification settings');
    }
  };

  return (
    <div className="flex flex-col items-center justify-center mx-auto h-screen">
      <div className="flex flex-col w-[762px] text-[Nunito Sans] px-[1px] h-full">
        {/* Header */}
        <div className="flex items-start mt-5 mb-2">
          <div className="text-start text-sm font-light text-[#75777C]">
            Workspace settings
          </div>
          <div className="text-start text-sm font-light text-[#222222] pl-2">
            / Notifications
          </div>
        </div>

        <ScrollArea maxHeight="calc(100vh - 80px)" className="pb-[250px]">
          <div className="flex flex-col">
            {/* Page Title */}
            <div className="flex flex-col mb-7">
              <div className="flex flex-col mt-5 text-left gap-[2px]">
                <div className="text-xl font-semibold text-[#222222]">Notifications</div>
                <div className="text-sm font-light text-[#75777C]">
                  Stay updated on your workspace activities
                </div>
              </div>
            </div>

            <Divider className="w-full bg-[#EEEFF1] mb-4" />

            {/* Filter Options */}
            <div className="flex items-center gap-4 mb-7">
              <button 
                onClick={() => setFilter('all')}
                className={`text-sm font-medium rounded-[8px] px-2 py-1 border border-[#E6E7EA] bg-[#FAFAFA] ${
                  filter === 'all' ? 'text-[#222222]' : 'text-[#75777C]'
                }`}
              >
                <span className="text-[#1565C0] mr-1">•</span>
                <span className="text-[#000000]">{notifications.length} All</span>
              </button>
              <button 
                onClick={() => setFilter('unread')}
                className={`text-sm font-medium rounded-[8px] px-2 py-1 border border-[#E6E7EA] bg-[#FAFAFA] ${
                  filter === 'unread' ? 'text-[#222222]' : 'text-[#75777C]'
                }`}
              >
                <span className="text-[#1565C0] mr-1">•</span>
                <span className="text-[#000000]">{unreadCount} unread</span>
              </button>
              <button 
                onClick={() => setFilter('recent')}
                className={`text-sm font-medium rounded-[8px] px-2 py-1 border border-[#E6E7EA] bg-[#FAFAFA] ${
                  filter === 'recent' ? 'text-[#222222]' : 'text-[#75777C]'
                }`}
              >
                Recent
              </button>
              <div className="flex-grow"></div>
              <button 
                onClick={() => {
                  setNotifications(notifications.map(n => ({ ...n, isRead: true })));
                }}
                className="text-sm text-[#75777C] flex items-center gap-1"
              >
                <img src={read} alt="Mark all as read" />
                Mark all as read
              </button>
            </div>

            {/* Notifications List */}
            <div className="flex flex-col gap-4 text-left">
              {getFilteredNotifications().map((notification, index) => (
                <div key={index} className="flex items-start gap-3 p-3 hover:bg-[#FAFAFA] rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-[#E8FFF0] flex items-center justify-center">
                    {notification.type.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-sm text-[#222222]">{notification.title}</p>
                    <span className="text-xs text-[#75777C]">{notification.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default NotificationsPage; 